import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import SelectAllCheckbox from '../../../components/atoms/forms/SelectAllCheckbox';
import StopRow, { isStopActionable } from './StopRow';
import { getStopRowKey } from './utils';
import { anchorWindowDisplay } from './windowDisplay';

// Renders the per-route stop list and owns native HTML5 drag-and-drop.
// Order is dictated by `displayedSequence` (derived in useRouteEdits from
// effective time windows). On drop, the parent receives the drop neighbor's
// stopKey so it can compute a prefilled window for the Pin modal.

// Columns: checkbox, drag, badge, location, totes, status, delivery window,
// action. Location uses `minmax(0, 1fr)` so it absorbs extra width on
// wide screens (no trailing empty space after the Action column) without
// contributing per-row variance to the row's intrinsic size — the `0`
// lower bound zeros the track's max-content contribution regardless of
// the cell's content, so every row's grid resolves identically against
// the parent's width. Long location ids ellipsis-truncate via the
// existing `.sr-name { overflow: hidden; text-overflow: ellipsis }`.
// Keep in sync with StopRow's GRID_COLUMNS.
const GRID_COLUMNS = '32px 24px 40px minmax(0, 1fr) 80px 150px 260px 60px';

const StyledDiv = styledComponent('div', {
  border: `1px solid ${theme.colors.borderLight}`,
  borderRadius: theme.radii.input,
  // Capped at ~8 rows worth; rows scroll inside while the column header
  // stays pinned. `overflow-y: auto` enables mouse-wheel scrolling.
  maxHeight: '60vh',
  overflowY: 'auto',

  '& .sl-header': {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    gap: theme.spacing.fieldGap,
    padding: '8px 14px',
    background: theme.colors.bgMuted,
    ...theme.typography.microLabel,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  // Select-all cell spans the checkbox + drag-handle + stop-number columns.
  // At the header's 10px uppercase setting, "Select all (NN)" needs more
  // room than 2 columns offer — spanning all 3 lets the label sit cleanly
  // against the LOCATION header. The per-row stop badge still communicates
  // the stop number, so dropping the "STOP" header label here is fine.
  '& .sl-header .sl-header-select-all': {
    gridColumn: 'span 3',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  '& .sl-header .sl-header-action': {
    textAlign: 'right',
  },
  '& .sl-empty': {
    padding: theme.spacing.bodyPadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },
});

function StopList({
  stops,
  displayedSequence,
  anchoredStops,
  removeSelections,
  bulkQueue,
  isLocked,
  isViewOnly,
  onDropNeighbor,
  onToggleBulkQueue,
  onUndoRemove,
  onPinClick,
  onRequestDelete,
}) {
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dropTargetIdx, setDropTargetIdx] = useState(null);

  const stopByKey = useMemo(() => {
    const m = new Map();
    stops.forEach((s) => m.set(getStopRowKey(s), s));
    return m;
  }, [stops]);

  const orderedStops = useMemo(
    () =>
      displayedSequence
        .map((stopKey) => {
          const stop = stopByKey.get(stopKey);
          if (!stop) return null;
          return { stop, stopKey };
        })
        .filter(Boolean),
    [displayedSequence, stopByKey],
  );

  // Stops a supervisor can act on (i.e. not stocked / not in-progress).
  // The select-all header checkbox toggles ONLY these — locked rows can't
  // be removed regardless, so including them in the bulk selection would
  // be misleading.
  const actionableStops = useMemo(
    () =>
      orderedStops.filter(({ stop }) => !isLocked && isStopActionable(stop)),
    [orderedStops, isLocked],
  );
  const actionableQueuedCount = useMemo(
    () =>
      actionableStops.filter(({ stopKey }) => bulkQueue.has(stopKey)).length,
    [actionableStops, bulkQueue],
  );
  const allActionableSelected =
    actionableStops.length > 0 &&
    actionableQueuedCount === actionableStops.length;

  const handleSelectAll = (checked) => {
    actionableStops.forEach(({ stopKey }) =>
      onToggleBulkQueue(stopKey, checked),
    );
  };

  const handleDragStart = useCallback(
    (idx) => (e) => {
      setDraggingIdx(idx);
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(idx));
      }
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    setDraggingIdx(null);
    setDropTargetIdx(null);
  }, []);

  const handleDragOver = useCallback(
    (idx) => (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      if (draggingIdx !== null && idx !== draggingIdx) {
        setDropTargetIdx(idx);
      }
    },
    [draggingIdx],
  );

  const handleDrop = useCallback(
    (idx) => (e) => {
      e.preventDefault();
      const fromIdx = draggingIdx;
      setDraggingIdx(null);
      setDropTargetIdx(null);
      if (fromIdx === null || fromIdx === idx) return;
      const movedKey = orderedStops[fromIdx]?.stopKey;
      const neighborKey = orderedStops[idx]?.stopKey;
      const movedStop = stopByKey.get(movedKey);
      if (!movedKey || !movedStop) return;
      onDropNeighbor({
        stop: movedStop,
        stopKey: movedKey,
        neighborKey,
      });
    },
    [draggingIdx, orderedStops, stopByKey, onDropNeighbor],
  );

  if (orderedStops.length === 0) {
    return (
      <StyledDiv>
        <div className="sl-empty">No stops on this route.</div>
      </StyledDiv>
    );
  }

  let selectAllTitle;
  if (actionableStops.length === 0) {
    selectAllTitle = 'No stops available to select';
  } else if (allActionableSelected) {
    selectAllTitle = 'Deselect all stops on this route';
  } else {
    selectAllTitle = `Select all ${actionableStops.length} stops on this route for bulk delete`;
  }

  return (
    <StyledDiv data-testid="stop-list">
      <div className="sl-header">
        <div className="sl-header-select-all">
          {!isViewOnly && (
            <SelectAllCheckbox
              selectedCount={actionableQueuedCount}
              totalCount={actionableStops.length}
              onChange={handleSelectAll}
              subject="stops on this route"
              title={selectAllTitle}
              size="sm"
              dataTestId="stop-list-select-all"
            />
          )}
        </div>
        <span>Location</span>
        <span>Totes</span>
        <span>Status</span>
        <span>Delivery Window</span>
        <span className="sl-header-action">Action</span>
      </div>
      {orderedStops.map(({ stop, stopKey }, idx) => {
        const anchor = anchoredStops.get(stopKey);
        // Anchored windows are "HH:MM" local from the modal; format them
        // with the stop's date so the cells always show date + time.
        const anchorDisplay = anchor ? anchorWindowDisplay(stop, anchor) : null;
        return (
          <StopRow
            key={stopKey}
            stop={stop}
            stopKey={stopKey}
            positionLabel={idx + 1}
            pendingRemoval={removeSelections.has(stopKey)}
            bulkQueued={bulkQueue.has(stopKey)}
            isAnchored={Boolean(anchor)}
            windowStart={
              anchorDisplay
                ? anchorDisplay.startTimeWindowDisplay
                : stop.startTimeWindowDisplay || ''
            }
            windowEnd={
              anchorDisplay
                ? anchorDisplay.endTimeWindowDisplay
                : stop.endTimeWindowDisplay || ''
            }
            // Supervisor-anchored windows override the 24h indicator —
            // they explicitly chose a time, so show that time, not "Open 24h".
            isOpen24h={Boolean(stop.isOpen24h) && !anchor}
            isDragging={draggingIdx === idx}
            isDropTarget={dropTargetIdx === idx}
            isLocked={isLocked}
            isViewOnly={isViewOnly}
            onToggleBulkQueue={(checked) => onToggleBulkQueue(stopKey, checked)}
            onUndoRemove={() => onUndoRemove(stopKey)}
            onPinClick={onPinClick}
            onRequestDelete={onRequestDelete}
            onDragStart={handleDragStart(idx)}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver(idx)}
            onDrop={handleDrop(idx)}
          />
        );
      })}
    </StyledDiv>
  );
}

StopList.propTypes = {
  stops: PropTypes.arrayOf(PropTypes.object).isRequired,
  displayedSequence: PropTypes.arrayOf(PropTypes.string).isRequired,
  anchoredStops: PropTypes.instanceOf(Map).isRequired,
  removeSelections: PropTypes.instanceOf(Map).isRequired,
  bulkQueue: PropTypes.instanceOf(Set).isRequired,
  isLocked: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  onDropNeighbor: PropTypes.func.isRequired,
  onToggleBulkQueue: PropTypes.func.isRequired,
  onUndoRemove: PropTypes.func.isRequired,
  onPinClick: PropTypes.func.isRequired,
  onRequestDelete: PropTypes.func.isRequired,
};

StopList.defaultProps = {
  isLocked: false,
  isViewOnly: false,
};

export default StopList;
